import { Divider, PopoverOrigin, Stack, Typography } from '@mui/material'
import { format, isBefore } from 'date-fns'
import { find } from 'lodash'
import { FC, MouseEvent, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ReplyScheduleDTO } from '../../../../api/gen/base'
import { useInvalidateReplyScheduleCalendarQuery } from '../../../../api/hooks/useInvalidateReplyScheduleCalendarQuery'
import ArrowRightIcon from '../../../../assets/icons/ArrowRight.svg?react'
import PenIcon from '../../../../assets/icons/Pen.svg?react'
import TrashIcon from '../../../../assets/icons/Trash.svg?react'
import { REQUEST_ROUTE } from '../../../../constants/routing.constants'
import { RegularRequestDeleteDialog } from '../../../../dialogs/RegularRequestDeleteDialog'
import {
    RegularRequestEditDialog,
    RegularRequestEditDialogData,
} from '../../../../dialogs/RegularRequestEditDialog'
import { useWrapperStore } from '../../../../store/storeWrapper'
import {
    getRegularRequestFormFilesFromReplyContent,
    getTextContentFromReplyContent,
} from '../../../../utils/regularRequests'
import { InfoPopover } from './InfoPopover'
import { PopoverFooterButton } from './PopoverFooterButton'

type Props = {
    anchorEl: HTMLElement | null
    onClose?: VoidFunction
    anchorOrigin?: PopoverOrigin
    transformOrigin?: PopoverOrigin
    data?: ReplyScheduleDTO
}

export const RegularRequestInfoPopover: FC<Props> = ({
    anchorEl,
    onClose,
    anchorOrigin,
    transformOrigin,
    data,
}) => {
    const navigate = useNavigate()

    const invalidateRequests = useInvalidateReplyScheduleCalendarQuery()

    const { projectListFull, setCurrentAgentAndAssistantIdDialog, fetchModelDetail } =
        useWrapperStore((state) => state)

    const [regularRequestEditDialogData, setRegularRequestEditDialogData] =
        useState<RegularRequestEditDialogData | null>(null)
    const [regularRequestDeleteDialogData, setRegularRequestDeleteDialogData] = useState<
        number | null
    >(null)

    const projectName = useMemo(() => {
        const projectDetail = find(projectListFull, (item) => {
            return item.id === data?.workplace_id
        })

        return projectDetail?.title ?? 'Без проекта'
    }, [projectListFull, data])

    const textContent = getTextContentFromReplyContent(data?.reply.content ?? [])
    const filesContent = getRegularRequestFormFilesFromReplyContent(data?.reply.content ?? [])

    const handleOpenEditRegularRequestDialog = () => {
        if (!data) {
            return
        }

        const {
            id,
            reply: { thread_id, assistant_id, model_slug },
            timing,
        } = data

        const currentAgentNameInDialog = model_slug ? model_slug : 'AUTO'

        setCurrentAgentAndAssistantIdDialog({
            agent: currentAgentNameInDialog,
            assistantId: assistant_id ?? undefined,
        })
        fetchModelDetail(currentAgentNameInDialog, assistant_id ?? undefined)

        setRegularRequestEditDialogData({
            text: textContent?.text ?? '',
            startDateTime: timing.send_time,
            repeatType: timing.repeat,
            repeatDays: timing.weekdays ?? undefined,
            files: filesContent,
            threadId: thread_id,
            replyScheduleId: id,
        })

        onClose?.()
    }

    const handleOpenDeleteRegularRequestDialog = () => {
        setRegularRequestDeleteDialogData(data?.id)
        onClose?.()
    }

    const handleCloseEditRegularRequestDialog = () => {
        setRegularRequestEditDialogData(null)
    }

    const handleCloseDeleteRegularRequestDialog = () => {
        setRegularRequestDeleteDialogData(null)
    }

    const handleRefetchDataOnSuccess = () => {
        invalidateRequests()
    }

    const handleOpenThread = (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation()

        navigate(`${REQUEST_ROUTE}/${data?.reply.thread_id}`)
        onClose?.()
    }

    const repeatInfo = useMemo(() => {
        if (data?.timing.repeat === 'EVERYDAY') {
            return 'Ежедневно'
        }

        if (data?.timing.repeat === 'WEEK_DAYS') {
            return 'По дням недели'
        }

        return 'Не повторять'
    }, [data])

    const canEdit = !!data && isBefore(new Date(), data.timing.send_time)

    return (
        <>
            <RegularRequestEditDialog
                open={!!regularRequestEditDialogData && !regularRequestDeleteDialogData}
                initialData={regularRequestEditDialogData}
                onClose={handleCloseEditRegularRequestDialog}
                onDelete={handleOpenDeleteRegularRequestDialog}
                onSubmit={handleRefetchDataOnSuccess}
            />
            <RegularRequestDeleteDialog
                open={!!regularRequestDeleteDialogData}
                replyScheduleId={regularRequestDeleteDialogData}
                onClose={handleCloseDeleteRegularRequestDialog}
                onSuccess={() => {
                    handleRefetchDataOnSuccess()
                    handleCloseEditRegularRequestDialog()
                }}
            />
            <InfoPopover
                anchorEl={anchorEl}
                onClose={onClose}
                anchorOrigin={anchorOrigin}
                transformOrigin={transformOrigin}
                title={textContent?.text ?? ''}
                footer={
                    canEdit ? (
                        <>
                            <PopoverFooterButton
                                startIcon={<TrashIcon color="#E82C1F" />}
                                onClick={handleOpenDeleteRegularRequestDialog}
                                sx={{
                                    minWidth: '55px',
                                    width: '55px',
                                }}
                            />
                            <Divider orientation="vertical" sx={{ height: '32px' }} />
                            <PopoverFooterButton
                                startIcon={<PenIcon color="#5A5F72" />}
                                onClick={handleOpenEditRegularRequestDialog}
                                sx={{
                                    minWidth: '55px',
                                    width: '55px',
                                }}
                            />
                            <Divider orientation="vertical" sx={{ height: '32px' }} />
                            <PopoverFooterButton
                                endIcon={<ArrowRightIcon color="#5A5F72" />}
                                onClick={handleOpenThread}
                                sx={{
                                    width: '100%',
                                    '.MuiButton-endIcon': {
                                        marginLeft: 0,
                                    },
                                }}>
                                Перейти к запросу
                            </PopoverFooterButton>
                        </>
                    ) : (
                        <PopoverFooterButton
                            endIcon={<ArrowRightIcon color="#5A5F72" />}
                            onClick={handleOpenThread}
                            sx={{
                                width: '100%',
                                '.MuiButton-endIcon': {
                                    marginLeft: 0,
                                },
                            }}>
                            Перейти к запросу
                        </PopoverFooterButton>
                    )
                }>
                <Stack direction="column" gap={1}>
                    <Stack direction="row" alignItems="center" gap="4px">
                        <Typography variant="body2" color="#5A5F72">
                            Проект:
                        </Typography>
                        <Typography
                            variant="body2"
                            color="#181B25"
                            sx={{
                                width: '100%',
                                display: '-webkit-box',
                                WebkitLineClamp: '1',
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}>
                            {projectName}
                        </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" gap="4px">
                        <Typography variant="body2" color="#5A5F72">
                            Дата начала:
                        </Typography>
                        <Typography variant="body2" color="#181B25">
                            {data?.timing?.send_time
                                ? format(data.timing.send_time, 'dd.MM.yyyy, HH:mm')
                                : ''}
                        </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" gap="4px">
                        <Typography variant="body2" color="#5A5F72">
                            Повтор:
                        </Typography>
                        <Typography variant="body2" color="#181B25">
                            {repeatInfo}
                        </Typography>
                    </Stack>
                    {!!filesContent.length && (
                        <Stack direction="row" alignItems="center" gap="4px">
                            <Typography variant="body2" color="#5A5F72">
                                Вложения:
                            </Typography>
                            <Typography variant="body2" color="#181B25">
                                {filesContent.length}
                            </Typography>
                        </Stack>
                    )}
                </Stack>
            </InfoPopover>
        </>
    )
}
